import i18n from '@/i18n';
import { GrievanceFormState } from '@/store/types';
import { FieldValidation, FormError } from '@/types';
import * as yup from '@/yup';
import zxcvbn from 'zxcvbn';
import _ from 'lodash';
import { ObjectSchema, ValidationError } from 'yup';
import {
    PASSWORD_MIN_LENGTH,
    PASSWORD_MAX_LENGTH,
    PASSWORD_REGEX,
    PASSWORD_MIN_SCORE,
} from '@/utils/passwordConstants';

export function clearErrors(errors: FormError, ...fields: string[]): FormError {
    return _.omit(errors, 'global', ...fields);
}

export function mapValidationError(error: ValidationError): FormError {
    return error.inner.reduce((res, errors) => {
        const path = errors.path;
        if (path) {
            res[path] = errors.errors;
        }
        return res;
    }, {} as FormError);
}

export async function validateSchema(schema: ObjectSchema<any>, content: any): Promise<FormError | null> {
    try {
        await schema.validate(content, { abortEarly: false });
    } catch (e) {
        if (e instanceof ValidationError) {
            return mapValidationError(e);
        }
    }
    return null;
}

export function shouldBeRequired(state: GrievanceFormState, getters: any, field: string): boolean {
    return getters.isFieldRequired(field);
}

export function refNoSchema() {
    return yup.string().test('is-a-ref-no-format', i18n.t('isRefNoFormat') as string, (val?: string | null) => {
        const refNoRegExp = /^\w{1,2}-\d+$/;
        return !!val?.match(refNoRegExp);
    });
}

export function passwordConfirmationSchema() {
    return yup.string().test('password_match', i18n.t('passwordMatch') as string, function(value) {
        return value === this.parent.password;
    });
}

export function passwordStrengthSchema() {
    return yup
        .string()
        .min(PASSWORD_MIN_LENGTH)
        .max(PASSWORD_MAX_LENGTH)
        .matches(PASSWORD_REGEX)
        .test('passwordStrength', i18n.t('passwordStrength') as string, value => {
            if (!value) return false;
            const passwordScore = zxcvbn(value).score;
            return passwordScore >= PASSWORD_MIN_SCORE;
        })
        .required();
}

export const forgotPasswordValidationSchema = yup.object({
    refNo: refNoSchema().required(),
    email: yup
        .string()
        .email()
        .required(),
});

export const resetPasswordValidationSchema = yup.object({
    password: passwordStrengthSchema(),
    passwordConfirmation: passwordConfirmationSchema(),
});

export const addFollowUpValidationSchema = yup.object({
    description: yup
        .string()
        .trim()
        .max(10000)
        .required(),
});

export const createGrievanceValidationSchema = (state: GrievanceFormState, getters: any) => {
    const baseStringSchema = (fieldValidations: FieldValidation[] = []) => {
        const maxLengthValidation = fieldValidations.find(validation => validation.type === 'maxlength');

        return yup
            .string()
            .trim()
            .max(maxLengthValidation?.argument || 10000);
    };
    const lazyRequirableStringSchema = (
        fieldId: string,
        isIdentified: boolean,
        fieldValidations: FieldValidation[] = [],
    ) =>
        yup.lazy(() => {
            const yupValidation = shouldBeRequired(state, getters, fieldId)
                ? isIdentified
                    ? baseStringSchema(fieldValidations).required()
                    : baseStringSchema(fieldValidations).when('anonymous', { is: false, then: yup.string().required() })
                : baseStringSchema(fieldValidations);

            return yupValidation;
        });
    return yup.object({
        type: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'type') ? yup.mixed().required() : yup.mixed();
        }),
        parentCategory: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'parentCategory') ? yup.mixed().required() : yup.mixed();
        }),
        category: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'category') ? yup.mixed().required() : yup.mixed();
        }),
        description: baseStringSchema(state.validations.description).required(),
        firstName: baseStringSchema(state.validations.firstName).when('anonymous', {
            is: false,
            then: yup.string().required(),
        }),
        lastName: baseStringSchema(state.validations.lastName).when('anonymous', {
            is: false,
            then: yup.string().required(),
        }),
        email: yup.string().when(['anonymous', 'notification'], (...args: any[]) => {
            const [anonymous, notification] = args;
            if (notification) {
                return baseStringSchema(state.validations.email)
                    .email()
                    .required();
            } else if (!anonymous) {
                return baseStringSchema(state.validations.email).email();
            }
            return yup.string();
        }),
        addressLine1: lazyRequirableStringSchema('addressLine1', false, state.validations.addressLine1),
        addressLine2: lazyRequirableStringSchema('addressLine2', false, state.validations.addressLine2),
        neighborhood: lazyRequirableStringSchema('neighborhood', false, state.validations.neighborhood),
        sublocality: lazyRequirableStringSchema('sublocality', false, state.validations.sublocality),
        city: lazyRequirableStringSchema('city', false, state.validations.city),
        state: lazyRequirableStringSchema('state', false, state.validations.state),
        country: lazyRequirableStringSchema('country', false, state.validations.country),
        postalCode: lazyRequirableStringSchema('postalCode', false, state.validations.postalCode),
        sublocality2: lazyRequirableStringSchema('sublocality2', false, state.validations.sublocality2),
        arealevel2: lazyRequirableStringSchema('arealevel2', false, state.validations.arealevel2),
        customTextarea1: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'customTextarea1') ? yup.string().required() : yup.mixed();
        }),
        customTextarea2: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'customTextarea2') ? yup.string().required() : yup.mixed();
        }),
        anonymous: yup.boolean().required(),
        notification: yup.boolean().required(),
        acceptTerms: yup.boolean().test('accepted', i18n.t('mustAcceptTerms') as string, val => !!val),
        customSingle2: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'customSingle2') ? yup.mixed().required() : yup.mixed();
        }),
        projectIdMap: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'projectIdMap') ? yup.mixed().required() : yup.mixed();
        }),
        customText1: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'customText1') ? yup.string().required() : yup.mixed();
        }),
        customText2: yup.lazy(() => {
            return shouldBeRequired(state, getters, 'customText2') ? yup.string().required() : yup.mixed();
        }),
    });
};

export const loginValidationSchema = yup.object({
    refNo: refNoSchema().required(),
    password: yup.string().required(),
});

export const confirmValidationSchema = yup.object({
    password: passwordStrengthSchema(),
    passwordConfirmation: passwordConfirmationSchema(),
});
